import React, { useEffect } from 'react';
import { Calendar, Clock, Video, CheckCircle, Shield } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import SEO from '../components/SEO';

const BookingPage = () => {
  const { language } = useLanguage();

  // Load Calendly script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const content = {
    en: {
      title: "Schedule Your Consultation",
      subtitle: "Book a 30-minute consultation with Attorney Jennifer N. Brantley",
      features: [
        { icon: Video, text: "Virtual meeting via Zoom" },
        { icon: Clock, text: "30-minute consultation" },
        { icon: CheckCircle, text: "Confirmation email with meeting link" },
      ],
      note: "Select a date and time that works for you. You'll receive a confirmation email with the Zoom meeting link.",
      disclaimer: "Scheduling a consultation does not create an attorney-client relationship."
    },
    es: {
      title: "Programe Su Consulta",
      subtitle: "Reserve una consulta de 30 minutos con la Abogada Jennifer N. Brantley",
      features: [
        { icon: Video, text: "Reunión virtual por Zoom" },
        { icon: Clock, text: "Consulta de 30 minutos" },
        { icon: CheckCircle, text: "Correo de confirmación con enlace de reunión" },
      ],
      note: "Seleccione una fecha y hora que le convenga. Recibirá un correo de confirmación con el enlace de la reunión de Zoom.",
      disclaimer: "Programar una consulta no crea una relación abogado-cliente."
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-[#FDFCFA]">
      <SEO 
        title="Book a Consultation"
        description="Schedule a 30-minute consultation with Attorney Jennifer N. Brantley. Virtual meetings available via Zoom. Get expert legal advice on trademarks, business formation, and contracts."
      />
      {/* Hero Section */}
      <section className="bg-[#1F2A44] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <Calendar className="w-12 h-12 sm:w-16 sm:h-16 text-[#C2A46D] mx-auto mb-4 sm:mb-6" />
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white mb-3 sm:mb-4">
            {t.title}
          </h1>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Info Sidebar */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="bg-white border border-[#E8E4DC] rounded-lg p-4 sm:p-6 sticky top-24">
              <h3 className="font-serif text-lg sm:text-xl text-[#1F2A44] mb-4 sm:mb-6">
                {language === 'es' ? 'Qué esperar' : 'What to Expect'}
              </h3>
              
              <div className="space-y-4">
                {t.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#1F2A44]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1F2A44]" />
                    </div>
                    <p className="text-sm sm:text-base text-[#555555] pt-1 sm:pt-2">{feature.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-[#E8E4DC]">
                <p className="text-xs sm:text-sm text-[#555555]">
                  {t.note}
                </p>
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    {t.disclaimer}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Calendly Widget */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="bg-white border border-[#E8E4DC] rounded-lg overflow-hidden">
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/jbrantley-jenniferbrantleylaw?primary_color=0a2f64"
                style={{ minWidth: '320px', height: '700px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-[#F5F3EF] py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="p-4">
              <p className="text-2xl sm:text-3xl font-serif text-[#1F2A44]">13+</p>
              <p className="text-xs sm:text-sm text-[#555555]">
                {language === 'es' ? 'Años de Experiencia' : 'Years of Experience'}
              </p>
            </div>
            <div className="p-4">
              <p className="text-2xl sm:text-3xl font-serif text-[#1F2A44]">500+</p>
              <p className="text-xs sm:text-sm text-[#555555]">
                {language === 'es' ? 'Clientes Atendidos' : 'Clients Served'}
              </p>
            </div>
            <div className="p-4">
              <p className="text-2xl sm:text-3xl font-serif text-[#1F2A44]">50</p>
              <p className="text-xs sm:text-sm text-[#555555]">
                {language === 'es' ? 'Estados para Marcas' : 'States for Trademarks'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
